/**
 * Corporate Theme — Dual palette (Dark / Light) for Admin Dashboard.
 *
 * DARK_NP  = Nautico Premium (yacht club, deep sea, gold accents)
 * LIGHT_NP = Corporate Blue  (SaaS-style, blue accents, light backgrounds)
 *
 * All admin components import from this file via AdminThemeContext.
 */

import {
  NP as DARK_NP,
  npFilterClass as darkFilterClass,
  npFilterBadge as darkFilterBadge,
  npStatusBadge as darkStatusBadge,
} from './nauticoTheme';

export { DARK_NP };

// ─── Light Corporate Palette ─────────────────────────────────────────────────
export const LIGHT_NP = {
  // Backgrounds
  bgRadial: 'bg-[#F8FAFC]',
  bgCard: 'bg-white',
  bgCardHover: 'hover:bg-[#F1F5F9]',

  // Card styles
  card: 'bg-white border border-[#E2E8F0] rounded-[18px] shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-all duration-250 ease-out',
  cardHover: 'hover:translate-y-[-4px] hover:shadow-[0_10px_25px_rgba(0,0,0,0.1)]',

  // Text
  textPrimary: 'text-[#0F172A]',
  textMuted: 'text-[#64748B]',
  textGold: 'text-[#3B82F6]',
  textSoftGold: 'text-[#1E40AF]',

  // Accent colors
  gold: '#3B82F6',
  softGold: '#1E40AF',
  success: '#16A34A',
  warning: '#D97706',
  danger: '#DC2626',
  info: '#2563EB',

  // KPI number style
  kpiValue: 'text-[34px] font-semibold tracking-tight text-[#0F172A]',

  // Trend badges
  trendUp: 'bg-[rgba(22,163,74,0.1)] text-[#16A34A]',
  trendDown: 'bg-[rgba(220,38,38,0.1)] text-[#DC2626]',

  // Table
  tableRowHover: 'hover:bg-[#F8FAFC]',
  tableHeader: 'text-[#64748B] text-xs font-semibold uppercase tracking-wider border-b border-[#E2E8F0]',
  tableCell: 'border-b border-[#F1F5F9]',

  // Badge variants
  badgeActive: 'bg-[rgba(22,163,74,0.1)] text-[#16A34A] border border-[rgba(22,163,74,0.2)]',
  badgePending: 'bg-[rgba(217,119,6,0.1)] text-[#D97706] border border-[rgba(217,119,6,0.2)]',
  badgeDanger: 'bg-[rgba(220,38,38,0.1)] text-[#DC2626] border border-[rgba(220,38,38,0.2)]',
  badgeInfo: 'bg-[rgba(37,99,235,0.1)] text-[#2563EB] border border-[rgba(37,99,235,0.2)]',
  badgeNeutral: 'bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0]',

  // Buttons
  btnGold: 'bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white font-bold rounded-xl transition-all duration-250 hover:translate-y-[-2px] hover:shadow-[0_0_20px_rgba(59,130,246,0.3)]',
  btnGhost: 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-all duration-250',
  btnDanger: 'bg-[rgba(220,38,38,0.1)] text-[#DC2626] hover:bg-[rgba(220,38,38,0.2)] rounded-xl transition-all duration-250',

  // Inputs
  input: 'bg-white border border-[#CBD5E1] rounded-xl text-[#0F172A] placeholder-[#94A3B8] focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] outline-none transition-all duration-250',
  select: 'bg-white border border-[#CBD5E1] rounded-xl text-[#0F172A] focus:border-[#3B82F6] outline-none transition-all duration-250',

  // Tabs
  tabActive: 'bg-[rgba(59,130,246,0.1)] text-[#3B82F6] border-b-2 border-[#3B82F6]',
  tabInactive: 'text-[#94A3B8] border-b-2 border-transparent hover:text-[#64748B] hover:bg-[#F8FAFC]',

  // Filters
  filterActive: 'bg-[rgba(59,130,246,0.1)] text-[#3B82F6] border border-[rgba(59,130,246,0.3)]',
  filterInactive: 'bg-white text-[#64748B] border border-[#E2E8F0] hover:border-[rgba(59,130,246,0.3)] hover:text-[#3B82F6]',

  // Modal
  modalOverlay: 'bg-[rgba(15,23,42,0.5)] backdrop-blur-sm',
  modalCard: 'bg-white border border-[#E2E8F0] rounded-[24px] shadow-[0_24px_64px_rgba(0,0,0,0.15)]',

  // Divider
  divider: 'border-[#E2E8F0]',

  // Scrollbar
  scrollbar: '[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#CBD5E1] [&::-webkit-scrollbar-thumb]:rounded-full',

  // Accent light backgrounds
  accentInfoBg: 'bg-[rgba(37,99,235,0.08)]',
  accentInfoBorder: 'border-[rgba(37,99,235,0.2)]',
  accentInfoText: 'text-[#2563EB]',
  accentWarningBg: 'bg-[rgba(217,119,6,0.08)]',
  accentWarningBorder: 'border-[rgba(217,119,6,0.2)]',
  accentWarningText: 'text-[#D97706]',
  accentSuccessBg: 'bg-[rgba(22,163,74,0.08)]',
  accentSuccessBorder: 'border-[rgba(22,163,74,0.2)]',
  accentSuccessText: 'text-[#16A34A]',
  accentDangerBg: 'bg-[rgba(220,38,38,0.08)]',
  accentDangerBorder: 'border-[rgba(220,38,38,0.2)]',
  accentDangerText: 'text-[#DC2626]',
  bgSubtle: 'bg-[#F8FAFC]',
  bgMuted: 'bg-[#F1F5F9]',
  borderSubtle: 'border-[#E2E8F0]',

  // Chart colors
  chartGold: '#3B82F6',
  chartBlue: '#8B5CF6',
} as const;

// ─── Theme type ──────────────────────────────────────────────────────────────
export type ThemePalette = { [K in keyof typeof DARK_NP]: string };
export type ThemeMode = 'dark' | 'light';

// ─── Get theme values by mode ────────────────────────────────────────────────
export function getThemeValues(mode: ThemeMode) {
  const palette = mode === 'light' ? LIGHT_NP : DARK_NP;

  const filterClass = (isActive: boolean) =>
    `flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all duration-250 ${
      isActive ? palette.filterActive : palette.filterInactive
    }`;

  const filterBadge = (isActive: boolean) =>
    `text-xs px-1.5 py-0.5 rounded-full ${
      isActive
        ? (mode === 'light' ? 'bg-[rgba(59,130,246,0.15)]' : 'bg-[rgba(198,168,94,0.2)]')
        : (mode === 'light' ? 'bg-[#F1F5F9]' : 'bg-[rgba(255,255,255,0.06)]')
    }`;

  const statusBadge = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
      case 'COMPLETED':
      case 'APPROVED':
        return palette.badgeActive;
      case 'PENDING':
      case 'PENDING_REVIEW':
        return palette.badgePending;
      case 'CANCELLED':
      case 'REJECTED':
      case 'SUSPENDED':
        return palette.badgeDanger;
      default:
        return palette.badgeNeutral;
    }
  };

  return {
    NP: palette,
    npFilterClass: filterClass,
    npFilterBadge: filterBadge,
    npStatusBadge: statusBadge,
  };
}

// ─── Legacy exports (default to Dark for backward compatibility) ─────────────
export const NP = DARK_NP;
export const npFilterClass = darkFilterClass;
export const npFilterBadge = darkFilterBadge;
export const npStatusBadge = darkStatusBadge;
